import { RegistrationStatus } from 'components/tree/behavior/candidate/registration/RegistrationWorkflow';
import { DateTime } from 'luxon';
import { OptionModel } from 'models';

export type CandidateSuperviewerModel = {
  program: string | null;
  status: string | null;
  moda: string | null;
  typeFormation: string | null;
};

export type CandidateStatusModel = {
  code: string;
  label: string;
};

export type CandidateDocumentModel = {
  url: string;
  label: string;
};

export type CandidateMenuModel = {
  id: number | string;
  title: string;
  code: string | null;
  children: CandidateMenuModel[];
  documents: CandidateDocumentModel[];
};

export type CandidateProgramModel = {
  statuses: CandidateStatusModel[];
  code: string;
  title: string;
  description: {
    processed: string;
  };
  button: any;
  contact: any[];
};

export type CandidateCandidacyModel = {
  candidateId: number;
  candidacyId: number;
  candidateStatus: string;
  competitionCode: string;
  programCode: string;
  status: CandidateStatusModel['code'];
  sbtActive: boolean | null;
  sbtComplete: boolean | null;
  sbtToken: string | null;
  sbtLink: string | null;
  applicationProgress: number | null;
  applicationComplete: boolean | null;
  applicationUrl: string | null;
  withRegistration: boolean;
  program?: CandidateProgramModel | null;
  menu?: CandidateMenuModel | null;
  ready: boolean;
  codeModa?: string;
  typeFormation?: string;
  byPassRenunciation?: boolean;
};

export type CandidateParagraphModel = {
  title: string | null;
  appearance: string | null;
  semantic: string | null;
  type: string | null;
  media: null | {
    url: string | null;
    alt: string | null;
    title: string | null;
  };
  text: null | {
    processed: string | null;
  };
  link: null | {
    uri: string | null;
    title: string | null;
  };
  alignment: string | null;
  paragraphs: CandidateParagraphModel[];
};

export type CandidateContentModel = {
  id: number;
  title: string;
  content: CandidateParagraphModel[];
  sidebar: CandidateParagraphModel[];
};

export type CandidateProgramContactModel = {
  title: string;
  text: {
    processed: string;
  };
  link: any;
};

export function isCandidacyModel(candidacy: any): candidacy is CandidateCandidacyModel {
  return (
    null !== candidacy &&
    typeof candidacy === 'object' &&
    'candidateId' in candidacy &&
    candidacy.candidateId !== null &&
    'competitionCode' in candidacy &&
    candidacy.competitionCode !== null &&
    'programCode' in candidacy &&
    candidacy.programCode !== null &&
    'candidateStatus' in candidacy &&
    candidacy.candidateStatus !== null &&
    'status' in candidacy &&
    candidacy.status !== null
  );
}

export type RegistrationSignerParentModel = {
  status: string | null;
  lastName: string | null;
  firstName: string | null;
  email: string | null;
  telephone: string | null;
};

export type RegistrationSignerModel = {
  status: string | null;
  email: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  telephone: string;
  nationality: string | null;
  civility: string | null;
  birthLocation: string | null;
  address1: string | null;
  address2: string | null;
  zipCode: string | null;
  city: string | null;
  country: string | null;
  parents: RegistrationSignerParentModel[] | null;
  moda: string | null;
  campus: string | null;
  language: string | null;
  year: string | null;
};

export type RegistrationPaymentGeneralCondition = {
  id: string | null;
  fileId: string | null;
  fileName: string | null;
  url: string | null;
};

export type RegistrationSignerV1Model = RegistrationSignerModel & {
  discountPercent: number | null;
  sessionLabel: string | null;
  givenName: string | null;
  proRole: string | null;
  proCompanyName: string | null;
  proSiret: string | null;
  proIndustrySector: string | null;
  proWorkingPlace: string | null;
  proTelephone: string | null;
  proEmail: string | null;
  proShowCompanyName: boolean | null;
  fundingType: string | null;
  companyName: string | null;
  companySiret: string | null;
  companyAddress: string | null;
  companyRepresentativeFullName: string | null;
  companyRepresentativeRole: string | null;
  companyRepresentativeTelephone: string | null;
  companyRepresentativeEmail: string | null;
  otherBillingAddress: boolean | null;
  companyBillingAddress: string | null;
  companyBillingPicFullName: string | null;
  companyBillingPicRole: string | null;
  companyBillingPicTelephone: string | null;
  companyBillingPicEmail: string | null;
  companyAccountPayableLastName: string | null;
  companyAccountPayableGivenName: string | null;
  companyAccountPayableTelephone: string | null;
  companyAccountPayableEmail: string | null;
  companyPlatformName: string | null;
  companyPlatformWebsite: string | null;
  companyPlatformDirectContactFullName: string | null;
  companyPlatformTelephone: string | null;
  companyPlatformEmail: string | null;
  amountCpfParticipantTtc: number | null;
  amountCompanyTtc: number | null;
  companyPaymentOption1: boolean | null;
  companyPaymentOption2: boolean | null;
  externalCompanyInformation: string | null;
  amountOpcoTtc: number | null;
  opcoName: string | null;
  participantPaymentOption1: boolean | null;
  participantPaymentOption2: boolean | null;
  fundingTypeOptions: OptionModel[] | null;
  boursestotales: number | null;
};

export type RegistrationModel = {
  candidateId: string;
  candidacyId: string;
  status: RegistrationStatus;
  signatureShn: boolean;
  signatureForceModa: boolean;
  signatureDate: string | null;
  signatureMethod: string | null;
  refuseReason: string | null;
  refuseText: string | null;
  paymentBypass: boolean | null;
  paymentPrice: number | null;
  paymentRestAmountPrice: number | null;
  paymentAmountToPrice: number | null;
  paymentCurrency: string;
  paymentPlatform: string | null;
  paymentMethod: string | null;
  paymentOnline: boolean | null;
  paymentIban: string | null;
  paymentCheckText: string | null;
  paymentNoteText: string | null;
  signatureTextDescriptionTop: string | null;
  signatureTextDescriptionMiddle: string | null;
  signatureTextDescriptionBottom: string | null;
  signatureFormVersion: string | null;
  signatureUserSelectedModa: string | null;
  signatureFormTitle: string | null;
  signatureFormDescriptionTop: string | null;
  signatureFormDescriptionBottom: string | null;
  paymentDate: string | null;
  paymentReference: string | null;
  paymentReferenceID: string | null;
  paymentUrl: string | null;
  paymentHostedFormUrl: string | null;
  paymentGeneralCondition: RegistrationPaymentGeneralCondition | null;
  paymentCheckoutSession: string | null;
  logs: {
    status: string;
    createdAt: string;
    payload?: any;
  }[];
  bankTransferlogs: {
    paymentId: string;
    createdAt: string;
    guaranteedAt: string;
    amountTo: number;
    status: string;
  }[];
  signatureSigner: RegistrationSignerModel | RegistrationSignerV1Model | null;
  signatureTuitionFees: number | null;
  renunciable: boolean | null;
  renunciableConfigurationLevel: boolean | null;
  renunciation: boolean | null;
  renunciationDate: string | null;
};

export type RegistrationRenunciationModel = {
  lastName: string;
  firstName: string;
  signatureDate: DateTime;
  program: string;
  email: string;
  status: boolean;
};

export function isRegistrationModel(subject: any): subject is RegistrationModel {
  return (
    null !== subject &&
    typeof subject === 'object' &&
    'candidateId' in subject &&
    typeof subject.candidateId === 'string' &&
    'candidacyId' in subject &&
    typeof subject.candidacyId === 'string' &&
    'status' in subject &&
    typeof subject.status === 'string'
  );
}

export function isRegistrationRenunciationModel(
  subject: any,
): subject is RegistrationRenunciationModel {
  return (
    null !== subject &&
    typeof subject === 'object' &&
    'lastName' in subject &&
    typeof subject.lastName === 'string' &&
    'firstName' in subject &&
    typeof subject.firstName === 'string' &&
    'program' in subject &&
    typeof subject.program === 'string' &&
    'email' in subject &&
    typeof subject.email === 'string'
  );
}

export type RegistrationSignatureGlossaryModel = {
  fields: (keyof RegistrationSignerModel | keyof RegistrationSignerV1Model)[];
  campusOptions: OptionModel[];
  modaOptions: OptionModel[];
};

export function isDummyCandidacyModel(candidacy: CandidateCandidacyModel) {
  return candidacy.candidateStatus === 'dummy';
}

export function isPotentialCandidacyModel(candidacy: CandidateCandidacyModel) {
  return candidacy.candidateStatus === 'potential';
}
